import { Communication } from "@medplum/fhirtypes";

import { deriveDotPhrasePlainText, sanitizeDotPhraseBodyHtml } from "@/dot-phrases";
import { startsWithBlockTag } from "@/utils/rich-text";

import { MessagePreview } from "../types/inbox";

/**
 * Reads a message body, which is rich text from the dotphrase composer or plain text from the
 * Textarea composer and from messages sent before rich text.
 *
 * @param communication - the thread message.
 * @returns the sanitized HTML, or the plain text as stored.
 */
export function readMessageContent(communication: Communication): MessageContent {
  const content = communication.payload?.[0]?.contentString ?? "";

  if (!startsWithBlockTag(content)) return { kind: "text", text: content };

  return { kind: "html", html: sanitizeDotPhraseBodyHtml(content) };
}

/**
 * Reads a message body as one line of plain text, for a thread row's preview.
 *
 * @param communication - the thread message.
 * @returns the message text without markup, or an attachment preview when the message has no text.
 */
export function readMessagePreview(communication: Communication): MessagePreview {
  const content = communication.payload?.[0]?.contentString ?? "";

  // Reuse the dot-phrase plain-text projection so tags and entities strip the same way, then flatten it to one line.
  const text = deriveDotPhrasePlainText(content).replace(/\s+/g, " ").trim();
  return text ? { kind: "text", text } : { kind: "attachment" };
}

/** True when the message has no text to show, e.g. an attachment-only message. */
export function isMessageContentEmpty(content: MessageContent): boolean {
  return content.kind === "text" && content.text === "";
}

export type MessageContent = { kind: "html"; html: string } | { kind: "text"; text: string };
